/**
 * Constants for Eval creation form
 * Defines scorer and variant type constants for type checking
 *
 * Note: Scorer and variant mappings are now fetched from the API endpoint `getAvailableScorers`.
 * These constants are kept only for type definitions and minimal logic (e.g., variants requiring params).
 */

// Scorer types
export const SCORER_STRING = 'STRING' as const;
export const SCORER_NUMBER = 'NUMBER' as const;
export const SCORER_BOOLEAN = 'BOOLEAN' as const;
export const SCORER_ARRAY = 'ARRAY' as const;
export const SCORER_OBJECT = 'OBJECT' as const;
export const SCORER_LLM = 'LLM' as const;
export const SCORER_CUSTOM = 'CUSTOM' as const;

export type ScorerType =
  | typeof SCORER_STRING
  | typeof SCORER_NUMBER
  | typeof SCORER_BOOLEAN
  | typeof SCORER_ARRAY
  | typeof SCORER_OBJECT
  | typeof SCORER_LLM
  | typeof SCORER_CUSTOM;

// STRING variants
export const VARIANT_EXACT = 'EXACT' as const;
export const VARIANT_CASE_INSENSITIVE = 'CASE_INSENSITIVE' as const;
export const VARIANT_NOT_EQUALS = 'NOT_EQUALS' as const;
export const VARIANT_NOT_CONTAINS = 'NOT_CONTAINS' as const;
export const VARIANT_CONTAINS = 'CONTAINS' as const;
export const VARIANT_STARTS_WITH = 'STARTS_WITH' as const;
export const VARIANT_ENDS_WITH = 'ENDS_WITH' as const;
export const VARIANT_REGEX = 'REGEX' as const;
export const VARIANT_EMPTY = 'EMPTY' as const;
export const VARIANT_NOT_EMPTY = 'NOT_EMPTY' as const;

// NUMBER variants
export const VARIANT_EQUALS = 'EQUALS' as const;
export const VARIANT_NOT_EQUALS_NUMBER = 'NOT_EQUALS' as const;
export const VARIANT_GREATER_THAN = 'GREATER_THAN' as const;
export const VARIANT_GREATER_THAN_OR_EQUAL_TO =
  'GREATER_THAN_OR_EQUAL_TO' as const;
export const VARIANT_LESS_THAN = 'LESS_THAN' as const;
export const VARIANT_LESS_THAN_OR_EQUAL_TO = 'LESS_THAN_OR_EQUAL_TO' as const;
export const VARIANT_EXACT_TOLERANCE = 'EXACT_TOLERANCE' as const;
export const VARIANT_RANGE = 'RANGE' as const;
export const VARIANT_EMPTY_NUMBER = 'EMPTY' as const;
export const VARIANT_NOT_EMPTY_NUMBER = 'NOT_EMPTY' as const;

// BOOLEAN variants
export const VARIANT_EQUALS_BOOLEAN = 'EQUALS' as const;
export const VARIANT_EMPTY_BOOLEAN = 'EMPTY' as const;
export const VARIANT_NOT_EMPTY_BOOLEAN = 'NOT_EMPTY' as const;

// ARRAY variants
export const VARIANT_EQUALS_ARRAY = 'EQUALS' as const;
export const VARIANT_NOT_EQUALS_ARRAY = 'NOT_EQUALS' as const;
export const VARIANT_LENGTH_EQUALS = 'LENGTH_EQUALS' as const;
export const VARIANT_LENGTH_GREATER_THAN = 'LENGTH_GREATER_THAN' as const;
export const VARIANT_LENGTH_LESS_THAN = 'LENGTH_LESS_THAN' as const;
export const VARIANT_CONTAINS_ITEM = 'CONTAINS_ITEM' as const;
export const VARIANT_CONTAINS_ALL = 'CONTAINS_ALL' as const;
export const VARIANT_NOT_CONTAINS_ITEM = 'NOT_CONTAINS_ITEM' as const;
export const VARIANT_ALL_ITEMS_MATCH = 'ALL_ITEMS_MATCH' as const;
export const VARIANT_LENGTH_BETWEEN = 'LENGTH_BETWEEN' as const;
export const VARIANT_EMPTY_ARRAY = 'EMPTY' as const;
export const VARIANT_NOT_EMPTY_ARRAY = 'NOT_EMPTY' as const;

// OBJECT variants
export const VARIANT_HAS_REQUIRED_KEYS = 'HAS_REQUIRED_KEYS' as const;
export const VARIANT_EQUALS_OBJECT = 'EQUALS' as const;

/** Only variant used in the UI and API for scorer LLM */
export const VARIANT_LLM_JUDGE = 'LLM_JUDGE' as const;

/** Values for `expectedVerdict` inside expectedResult (LLM-as-judge) */
export const LLM_EXPECTED_VERDICT_PASSED = 'Passed' as const;
export const LLM_EXPECTED_VERDICT_NOT_PASSED = 'Not Passed' as const;

export type LlmExpectedVerdict =
  | typeof LLM_EXPECTED_VERDICT_PASSED
  | typeof LLM_EXPECTED_VERDICT_NOT_PASSED;

export type VariantType =
  | typeof VARIANT_EXACT
  | typeof VARIANT_CASE_INSENSITIVE
  | typeof VARIANT_NOT_EQUALS
  | typeof VARIANT_NOT_CONTAINS
  | typeof VARIANT_CONTAINS
  | typeof VARIANT_STARTS_WITH
  | typeof VARIANT_ENDS_WITH
  | typeof VARIANT_REGEX
  | typeof VARIANT_EMPTY
  | typeof VARIANT_NOT_EMPTY
  | typeof VARIANT_EQUALS
  | typeof VARIANT_GREATER_THAN
  | typeof VARIANT_GREATER_THAN_OR_EQUAL_TO
  | typeof VARIANT_LESS_THAN
  | typeof VARIANT_LESS_THAN_OR_EQUAL_TO
  | typeof VARIANT_EXACT_TOLERANCE
  | typeof VARIANT_RANGE
  | typeof VARIANT_LENGTH_EQUALS
  | typeof VARIANT_LENGTH_GREATER_THAN
  | typeof VARIANT_LENGTH_LESS_THAN
  | typeof VARIANT_CONTAINS_ITEM
  | typeof VARIANT_CONTAINS_ALL
  | typeof VARIANT_NOT_CONTAINS_ITEM
  | typeof VARIANT_ALL_ITEMS_MATCH
  | typeof VARIANT_LENGTH_BETWEEN
  | typeof VARIANT_HAS_REQUIRED_KEYS
  | typeof VARIANT_EQUALS_OBJECT
  | typeof VARIANT_EQUALS_ARRAY
  | typeof VARIANT_NOT_EQUALS_ARRAY
  | typeof VARIANT_EQUALS_BOOLEAN
  | '';

/**
 * Variants that require additional parameters
 * Note: API doesn't provide this information, so we keep this minimal set
 */
export const VARIANTS_REQUIRING_PARAMS = new Set<VariantType>([
  VARIANT_RANGE, // Requires min and max
  VARIANT_EXACT_TOLERANCE, // Requires value and tolerance
  VARIANT_ALL_ITEMS_MATCH, // Requires condition and value
  VARIANT_LENGTH_BETWEEN // Requires min and max
]);
